// src/ui/components/UseAppBanner.jsx

import React, { useState } from 'react';
import FundDialog from './FundDialog';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.filatom.app';

// NOTE: your second brand color was written as "329fc" (5 chars — not a
// valid hex). I've used #3B29FC (a vivid indigo/blue) as the closest sensible
// match. Just swap the BLUE constant below if you meant a different shade.
const ORANGE = '#FF8C08';
const BLUE = '#3B29FC';

// ── UseAppBanner ─────────────────────────────────────────────────────────
// Animated, dismissible top banner with two actions on the left
// ("Get Android App" -> Play Store, "Support Startup" -> Fund dialog)
// and a "Cancel" dismiss button on the right.
// Re-appears on every fresh page load (no persistence), since it's just
// local component state that resets on refresh.

export const UseAppBanner = () => {
  const [visible, setVisible] = useState(true);
  const [closing, setClosing] = useState(false);
  const [fundOpen, setFundOpen] = useState(false);

  if (!visible) return null;

  const handleOpenPlayStore = (e) => {
    e.stopPropagation();
    window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
  };

  const handleOpenFund = (e) => {
    e.stopPropagation();
    setFundOpen(true);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setClosing(true);
    // Match the slide-up animation duration below before unmounting
    setTimeout(() => setVisible(false), 280);
  };

  const pillButtonStyle = {
    backgroundColor: 'rgba(0,0,0,0.28)',
    color: '#fff',
    fontSize: 10.5,
    fontWeight: 700,
    padding: '5px 12px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
    letterSpacing: 0.3,
    flexShrink: 0,
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <>
      <style>{`
        @keyframes useAppBannerSlideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0);      opacity: 1; }
        }
        @keyframes useAppBannerSlideUp {
          from { transform: translateY(0);      opacity: 1; }
          to   { transform: translateY(-100%);  opacity: 0; }
        }
        @keyframes useAppBannerGradientShift {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes useAppBannerPulseGlow {
          0%, 100% { box-shadow: 0 0 10px rgba(255,140,8,0.35), 0 0 0px rgba(59,41,252,0); }
          50%      { box-shadow: 0 0 18px rgba(255,140,8,0.5), 0 0 22px rgba(59,41,252,0.4); }
        }
        .use-app-banner {
          background: linear-gradient(270deg, ${ORANGE}, ${BLUE}, ${ORANGE});
          background-size: 400% 400%;
          animation:
            useAppBannerSlideDown 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards,
            useAppBannerGradientShift 6s ease-in-out infinite,
            useAppBannerPulseGlow 2.8s ease-in-out infinite;
        }
        .use-app-banner.closing {
          animation: useAppBannerSlideUp 0.28s ease forwards !important;
        }
        .use-app-banner:active {
          filter: brightness(0.95);
        }
      `}</style>
      <div
        className={`use-app-banner${closing ? ' closing' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 70,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '10px 14px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <button
            onClick={handleOpenPlayStore}
            style={pillButtonStyle}
          >
            Get Android App
          </button>
          <button
            onClick={handleOpenFund}
            style={pillButtonStyle}
          >
            Support Startup
          </button>
        </div>

        <button
          onClick={handleClose}
          aria-label="Cancel"
          style={{
            background: 'rgba(0,0,0,0.28)',
            border: 'none',
            borderRadius: 999,
            padding: '5px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          Cancel
        </button>
      </div>

      <FundDialog isOpen={fundOpen} onClose={() => setFundOpen(false)} />
    </>
  );
};

export default UseAppBanner;